import { useState, useEffect, useRef } from 'react';

const whatWeHelp = [
  'Gain clarity about what you truly want',
  'Identify what has been holding you back',
  'Build a personalized action plan',
  'Develop confidence and consistency',
  'Create real momentum toward your goals',
];

export default function SolutionSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="solution" className="bg-[#FAF8F5] py-24 md:py-32" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div
          className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <span
            className="text-xs font-semibold tracking-widest uppercase text-[#D4896A] block mb-4"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            About Us
          </span>
          <h2
            className="text-4xl md:text-5xl font-bold text-[#2C2C2C]"
            style={{ fontFamily: '\'Playfair Display\', serif' }}
          >
            A Clearer Path Starts Here
          </h2>
        </div>

        {/* Two-Column Layout */}
        <div className="flex flex-col lg:flex-row gap-8 items-stretch">
          {/* Left — Dark Card */}
          <div
            className={`lg:w-5/12 bg-[#2D3E2E] rounded-3xl p-10 md:p-14 text-white flex flex-col justify-center transition-all duration-700 delay-150 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <p
              className="text-xl md:text-2xl text-white/80 mb-8 leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              At Brand New You, we help individuals like you discover practical strategies
              to move toward your goals with purpose and confidence.
            </p>
            <div className="space-y-5 border-t border-white/20 pt-8">
              <div>
                <p className="text-xl text-white/60 italic" style={{ fontFamily: '\'Playfair Display\', serif' }}>
                  This is not about perfection.
                </p>
                <p className="text-xl font-bold text-[#E8C4A8]" style={{ fontFamily: '\'Playfair Display\', serif' }}>
                  It is about progress.
                </p>
              </div>
              <div>
                <p className="text-xl text-white/60 italic" style={{ fontFamily: '\'Playfair Display\', serif' }}>
                  This is not about pressure.
                </p>
                <p className="text-xl font-bold text-[#E8C4A8]" style={{ fontFamily: '\'Playfair Display\', serif' }}>
                  It is about having the support you need to move forward faster than you thought possible.
                </p>
              </div>
            </div>
          </div>

          {/* Right — Light Card */}
          <div
            className={`lg:w-7/12 bg-white rounded-3xl p-10 md:p-14 flex flex-col justify-center transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <p
              className="text-sm font-semibold text-[#D4896A] uppercase tracking-widest mb-8"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              What we help you do:
            </p>
            <div className="space-y-5">
              {whatWeHelp.map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-5 group"
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="w-8 h-8 rounded-full bg-[#F5EDE6] flex items-center justify-center shrink-0 group-hover:bg-[#D4896A] transition-colors duration-300">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-[#D4896A] group-hover:text-white text-sm transition-colors duration-300" />
                    </div>
                  </div>
                  <p
                    className="text-lg text-[#2C2C2C] leading-relaxed"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
